import React, { useEffect, useState } from "react";
import {
  Box, Card, CardHeader, TextField, Button,
  Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Avatar, Divider,
  Stack, TablePagination, Paper, Grid, CircularProgress, Backdrop,
  Dialog, DialogTitle, DialogContent, DialogActions, Zoom
} from "@mui/material";
import { Edit, Delete, CloudUpload, Add, Search, Close } from "@mui/icons-material";

import {
  collection, addDoc, getDocs, deleteDoc,
  doc, updateDoc, serverTimestamp,
  orderBy, query, where, getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

/* ---------- Premium Counter Component ---------- */
const Counter = ({ label, value, min = 0, onChange }) => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: colors.darkGreen, borderRadius: "12px", px: 2, py: 1, height: 48,
    border: `1px solid ${colors.goldenrod}`, transition: "all 0.2s",
    "&:hover": { borderColor: colors.gold }
  }}>
    <Typography fontSize={13} fontWeight={600} sx={{ color: colors.vanilla }}>{label} *</Typography>
    <Box sx={{
      display: "flex", alignItems: "center", background: colors.calPolyGreen,
      borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, p: 0.5
    }}>
      <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: "6px", color: colors.vanilla }}
        onClick={() => onChange(Math.max(min, value - 1))}>−</IconButton>
      <Box sx={{ width: 32, textAlign: "center", fontWeight: 700, fontSize: 13, color: colors.vanilla }}>{value}</Box>
      <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: "6px", color: colors.vanilla }}
        onClick={() => onChange(value + 1)}>+</IconButton>
    </Box>
  </Box>
);

function Roomdetails() {
  const initialForm = {
    roomTypeName: "",
    cmId: "",
    description: "",
    adults: 2,
    children: 0,
    beds: 1,
    bedType: "",
    price: "",
    discount: 0,
    imageUrl: ""
  };

  const [form, setForm] = useState(initialForm);
  const [rooms, setRooms] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const auth = getAuth();
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserData(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!userData) return;
      try {
        const roleRef = doc(db, "authroles", userData.uid);
        const snap = await getDoc(roleRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.propertyId) {
            setPropertyId(data.propertyId);
          }
        }
      } catch (err) {
        console.error("Error fetching user metadata:", err);
      }
    };
    fetchUserRole();
  }, [userData]);

  const fetchRooms = async () => {
    if (!userData || !propertyId) return;
    try {
      const q = query(
        collection(db, "rooms"),
        where("propertyId", "==", propertyId),
        orderBy("roomTypeName", "asc")
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
      setRooms(list);
    } catch (err) {
      console.error("Error standardizing collection snap:", err);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [userData, propertyId]);

  /* ---------- Validation Engine ---------- */
  const validate = () => {
    let e = {};
    if (!form.roomTypeName.trim()) e.roomTypeName = "Room Type is explicit";
    if (!form.cmId.trim()) e.cmId = "Channel Manager ID parameter required";
    if (!form.bedType.trim()) e.bedType = "Structural assignment layout required";
    if (form.discount < 0 || form.discount > 100) e.discount = "Bound 0–100%";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, image: null }));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return form.imageUrl || "";
    const imgRef = ref(storage, `rooms/${Date.now()}_${imageFile.name}`);
    await uploadBytes(imgRef, imageFile);
    return await getDownloadURL(imgRef);
  };

  const handleOpenCreate = () => {
    setForm(initialForm);
    setImageFile(null);
    setImagePreview("");
    setEditId(null);
    setErrors({});
    setOpenModal(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!userData || !propertyId) {
      alert("Session profile missing architecture bounds.");
      return;
    }

    try {
      setIsSubmitting(true);
      const imageUrl = await uploadImage();
      const basePriceNum = Number(form.price);
      const discountNum = Number(form.discount);
      const finalPrice = basePriceNum - (basePriceNum * discountNum) / 100;

      const payload = {
        userId: userData.uid,
        propertyId: propertyId,
        roomTypeName: form.roomTypeName.trim(),
        cmId: form.cmId.trim(),
        description: form.description.trim(),
        capacity: {
          adults: form.adults,
          children: form.children,
          total: form.adults + form.children
        },
        bed: {
          type: form.bedType.trim(),
          count: form.beds
        },
        pricing: {
          basePrice: basePriceNum,
          discount: discountNum,
          finalPrice: finalPrice
        },
        imageUrl,
        updatedAt: serverTimestamp()
      };

      if (editId) {
        await updateDoc(doc(db, "rooms", editId), payload);
      } else {
        await addDoc(collection(db, "rooms"), {
          ...payload,
          createdAt: serverTimestamp()
        });
      }

      setOpenModal(false);
      setForm(initialForm);
      setEditId(null);
      setImageFile(null);
      setImagePreview("");
      fetchRooms();
    } catch (error) {
      console.error("Submission operational failure:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (r) => {
    setForm({
      roomTypeName: r.roomTypeName || "",
      cmId: r.cmId || "",
      description: r.description || "",
      adults: r.capacity?.adults || 2,
      children: r.capacity?.children || 0,
      beds: r.bed?.count || 1,
      bedType: r.bed?.type || "",
      price: r.pricing?.basePrice || "",
      discount: r.pricing?.discount || 0,
      imageUrl: r.imageUrl || ""
    });
    setImagePreview(r.imageUrl || "");
    setEditId(r.id);
    setErrors({});
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Purge asset from local configuration tables?")) {
      try {
        setIsSubmitting(true);
        await deleteDoc(doc(db, "rooms", id));
        fetchRooms();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const filteredRooms = rooms.filter(r =>
    r.roomTypeName?.toLowerCase().includes(search.toLowerCase()) ||
    r.cmId?.toLowerCase().includes(search.toLowerCase()) ||
    r.bed?.type?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedRooms = filteredRooms.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1301 }} open={isSubmitting}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" thickness={4.5} />
          <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: "0.5px" }}>Syncing Database Layers...</Typography>
        </Stack>
      </Backdrop>

      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", mx: "auto", width: "100%" }}>

        {/* HEADER MODULE */}
        <Box mb={3} display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: colors.vanilla, letterSpacing: "-1px" }}>
              Room Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              borderRadius: "12px",
              px: 3,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.goldenrod,
              color: colors.darkGreen,
              boxShadow: "0 4px 12px rgba(209, 165, 5, 0.2)",
              "&:hover": { bgcolor: colors.gold }
            }}
          >
            Provision New Type
          </Button>
        </Box>

        {/* CENTRAL DATA METRICS VIEW */}
        <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 30px rgba(0,0,0,0.3)", border: `1px solid ${colors.goldenrod}`, background: colors.calPolyGreen, overflow: "hidden" }}>
          <CardHeader
            title="Configured Room Matrix"
            titleTypographyProps={{ fontSize: "16px", fontWeight: 700, color: colors.vanilla }}
            action={
              <TextField
                size="small"
                placeholder="Filter room types, CM ID..."
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{
                  style: { color: colors.vanilla },
                  startAdornment: <Search fontSize="small" sx={{ color: colors.goldenrod, mr: 1 }} />,
                }}
                sx={{
                  width: { xs: "100%", sm: 280 },
                  bgcolor: colors.darkGreen,
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.goldenrod },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.gold },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.gold }
                }}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            }
            sx={{ py: 2.5, px: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: { xs: "stretch", sm: "center" } }}
          />
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

          <Table component={Paper} elevation={0} sx={{ borderRadius: 0, backgroundColor: "transparent" }}>
            <TableHead sx={{ background: colors.darkGreen }}>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 700, color: colors.vanilla, width: 100 }}>Actions</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.vanilla, width: 80 }}>Index</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.vanilla }}>Classification Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.vanilla }}>CM Reference ID</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: colors.vanilla }}>Adults Bound</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: colors.vanilla }}>Children Bound</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: colors.vanilla }}>Beds Count</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.vanilla }}>Layout Spec</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: colors.vanilla }}>Visual Profile</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRooms.map((r, index) => (
                <TableRow key={r.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: "background 0.2s", '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                  <TableCell align="center" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="primary" size="small" onClick={() => handleEdit(r)} sx={{ bgcolor: colors.darkGreen, color: colors.gold, borderRadius: "8px", '&:hover': { bgcolor: colors.calPolyGreen } }}>
                        <Edit fontSize="inherit" style={{ fontSize: "16px" }} />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleDelete(r.id)} sx={{ bgcolor: colors.darkGreen, borderRadius: "8px", '&:hover': { bgcolor: colors.calPolyGreen } }}>
                        <Delete fontSize="inherit" style={{ fontSize: "16px" }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.vanilla, opacity: 0.8, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: colors.gold, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                    {r.roomTypeName}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Monospace", color: colors.vanilla, fontWeight: 600, fontSize: "13px", borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                    {r.cmId || "—"}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.capacity?.adults}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.capacity?.children}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.bed?.count}</TableCell>
                  <TableCell sx={{ color: colors.vanilla, fontWeight: 500, fontSize: "13px", opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.bed?.type || "—"}</TableCell>
                  <TableCell align="center" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                    {r.imageUrl ? (
                      <Avatar src={r.imageUrl} variant="rounded" sx={{ width: 44, height: 44, border: `1px solid ${colors.goldenrod}`, mx: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: colors.vanilla, opacity: 0.5 }} fontWeight={600}>No Asset</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {filteredRooms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" sx={{ color: colors.vanilla, opacity: 0.7 }} fontWeight={500}>
                      No configuration definitions matching evaluation logic found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredRooms.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ borderTop: `1px solid ${colors.goldenrod}`, color: colors.vanilla, '& .MuiTablePagination-selectIcon': { color: colors.vanilla } }}
          />
        </Card>
      </Box>

      {/* POPUP PROVISIONING MODAL CONTAINER */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        TransitionComponent={Zoom}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 24px 50px rgba(0,0,0,0.5)",
            border: `1px solid ${colors.goldenrod}`,
            bgcolor: colors.calPolyGreen,
            color: colors.vanilla,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: colors.darkGreen, py: 2.5, px: 3 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: colors.vanilla }}>
            {editId ? "🔧 Adjust Structural Properties" : "✨ Provision New Structural Type"}
          </Typography>
          <IconButton onClick={() => setOpenModal(false)} size="small" sx={{ color: colors.vanilla }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

        <DialogContent sx={{ p: 3, bgcolor: colors.calPolyGreen }}>
          <div className="container-fluid p-0">
            <div className="row">
              <div className="col-sm-6 mb-3">
                <TextField
                  size="small" label="Room Type Designation" fullWidth required
                  error={!!errors.roomTypeName} helperText={errors.roomTypeName}
                  value={form.roomTypeName}
                  onChange={e => setForm({ ...form, roomTypeName: e.target.value })}
                  InputLabelProps={{ style: { color: colors.vanilla } }}
                  InputProps={{ style: { color: colors.vanilla } }}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      borderRadius: "8px",
                      backgroundColor: colors.darkGreen,
                      "& fieldset": { borderColor: colors.goldenrod },
                      "&:hover fieldset": { borderColor: colors.gold },
                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                    } 
                  }}
                />
              </div>
              <div className="col-sm-6 mb-3">
                <TextField
                  size="small" label="Channel Manager ID (cmId)" fullWidth required
                  error={!!errors.cmId} helperText={errors.cmId}
                  value={form.cmId}
                  onChange={e => setForm({ ...form, cmId: e.target.value })}
                  placeholder="e.g. CM-ROOM-DX10"
                  InputLabelProps={{ style: { color: colors.vanilla } }}
                  InputProps={{ style: { color: colors.vanilla } }}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      borderRadius: "8px",
                      backgroundColor: colors.darkGreen,
                      "& fieldset": { borderColor: colors.goldenrod },
                      "&:hover fieldset": { borderColor: colors.gold },
                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                    } 
                  }}
                />
              </div>

              <div className="col-sm-4 mb-3">
                <Counter label="Beds Config" value={form.beds} min={1} onChange={v => setForm({ ...form, beds: v })} />
              </div>
              <div className="col-sm-4 mb-3">
                <Counter label="Max Adults" value={form.adults} min={1} onChange={v => setForm({ ...form, adults: v })} />
              </div>
              <div className="col-sm-4 mb-3">
                <Counter label="Max Children" value={form.children} onChange={v => setForm({ ...form, children: v })} />
              </div>

              <div className="col-sm-12 mb-3">
                <TextField
                  size="small" label="Bed Dimensions / Type" fullWidth required
                  error={!!errors.bedType} helperText={errors.bedType}
                  value={form.bedType}
                  onChange={e => setForm({ ...form, bedType: e.target.value })}
                  placeholder="e.g. King Suite"
                  InputLabelProps={{ style: { color: colors.vanilla } }}
                  InputProps={{ style: { color: colors.vanilla } }}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      borderRadius: "8px",
                      backgroundColor: colors.darkGreen,
                      "& fieldset": { borderColor: colors.goldenrod },
                      "&:hover fieldset": { borderColor: colors.gold },
                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                    } 
                  }}
                />
              </div>

              <div className="col-sm-12 mb-3">
                <TextField
                  size="small" label="Inventory Profile Layout Description" multiline rows={3} fullWidth
                  error={!!errors.description} helperText={errors.description}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  InputLabelProps={{ style: { color: colors.vanilla } }}
                  InputProps={{ style: { color: colors.vanilla } }}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      borderRadius: "8px",
                      backgroundColor: colors.darkGreen,
                      "& fieldset": { borderColor: colors.goldenrod },
                      "&:hover fieldset": { borderColor: colors.gold },
                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                    } 
                  }}
                />
              </div>

              <div className="col-sm-12 mb-3">
                <Typography variant="body2" fontWeight={600} sx={{ color: errors.image ? "#d32f2f" : colors.vanilla }} mb={1}>
                  Structural Asset Visual Profile * {errors.image && `(${errors.image})`}
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    sx={{
                      height: 56,
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderRadius: "12px",
                      px: 3,
                      textTransform: "none",
                      borderColor: errors.image ? "#d32f2f" : colors.goldenrod,
                      color: colors.vanilla
                    }}
                  >
                    Upload Architecture Image
                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                  </Button>
                  {imagePreview && (
                    <Avatar
                      src={imagePreview}
                      variant="rounded"
                      sx={{
                        width: 56,
                        height: 56,
                        border: `2px solid ${colors.vanilla}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                        borderRadius: "12px"
                      }}
                    />
                  )}
                </Stack>
              </div>
            </div>
          </div>
        </DialogContent>
        <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

        <DialogActions sx={{ p: 2.5, bgcolor: colors.darkGreen, gap: 1.5 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: colors.vanilla, borderRadius: "10px" }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            sx={{
              borderRadius: "10px",
              px: 4,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.goldenrod,
              color: colors.darkGreen,
              '&:hover': { bgcolor: colors.gold }
            }}
          >
            {editId ? "Update Specification" : "Commit Parameters"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Roomdetails;